const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getBaseUrl = () => {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured. Please set REACT_APP_API_BASE_URL.');
  }
  return API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
};

const handleResponse = async (response) => {
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }
  if (json && json.success === false) {
    throw new Error(json?.message || 'API responded with an error.');
  }
  return json;
};

export const globalMasterService = {
  async getStates() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=state`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    // Extract stateName from details.stateName and filter by active status
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => item?.details?.stateName || '')
      .filter(Boolean); // Remove any empty state names
  },

  async getCities() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=city`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    // Extract city data and filter by active status
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        cityName: item?.details?.cityName || '',
        state: item?.details?.state || '',
        id: item?._id || '',
      }))
      .filter(item => item.cityName); // Remove any empty city names
  },

  async getPincodes(cityFilter = null, stateFilter = null) {
    const base = getBaseUrl();
    let url = `${base}/api/globalMasterDetails?type=pincode`;

    // Add filters if provided
    const params = [];
    if (cityFilter) params.push(`city=${encodeURIComponent(cityFilter)}`);
    if (stateFilter) params.push(`state=${encodeURIComponent(stateFilter)}`);
    if (params.length > 0) url += `&${params.join('&')}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    // Extract pincode data and filter by active status
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        pincode: item?.details?.pincode || '',
        city: item?.details?.city || '',
        state: item?.details?.state || '',
        id: item?._id || '',
      }))
      .filter(item => item.pincode); // Remove any empty pincodes
  },

  async getMaritalStatuses() {
    const base = getBaseUrl();
    try {
      const response = await fetch(`${base}/api/globalMasterDetails?type=maritalStatus`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await handleResponse(response);
      const data = Array.isArray(json?.data) ? json.data : [];

      // Extract API marital status data and filter by active status
      const apiStatuses = data
        .filter(item => item?.status === true && !item?.isDeleted)
        .map(item => ({
          value: item?.details?.maritalStatus || item?.details?.statusName || '',
          label: item?.details?.maritalStatus || item?.details?.statusName || '',
          id: item?._id || '',
          source: 'api'
        }))
        .filter(item => item.value); // Remove any empty values

      // Ensure essential marital statuses are always available
      const essentialStatuses = [
        { value: 'Single', label: 'Single', id: 'single', source: 'default' },
        { value: 'Married', label: 'Married', id: 'married', source: 'default' },
        { value: 'Other', label: 'Other', id: 'other', source: 'default' }
      ];

      // Merge API statuses with essential statuses, preferring API data when available
      const mergedStatuses = [...essentialStatuses];

      // Add API statuses that aren't already covered by essentials
      apiStatuses.forEach(apiStatus => {
        const exists = mergedStatuses.some(status =>
          status.value.toLowerCase() === apiStatus.value.toLowerCase()
        );
        if (!exists) {
          mergedStatuses.push(apiStatus);
        }
      });

      return mergedStatuses;
    } catch (error) {
      console.error('Failed to fetch marital statuses from API, using defaults:', error);
      // Return essential statuses if API fails
      return [
        { value: 'Single', label: 'Single', id: 'single', source: 'default' },
        { value: 'Married', label: 'Married', id: 'married', source: 'default' },
        { value: 'Other', label: 'Other', id: 'other', source: 'default' }
      ];
    }
  },

  async getProjectProgress() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=projectProgress`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    // Extract project progress data and filter by active status
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        value: item?.details?.progressName || item?.details?.projectProgressName || item?.projectProgressName || item?.progressName || item?.name || item?.value || '',
        label: item?.details?.progressName || item?.details?.projectProgressName || item?.projectProgressName || item?.progressName || item?.name || item?.value || '',
        id: item?._id || '',
      }))
      .filter(item => item.value); // Remove any empty values
  },

  async getProgress() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=progress`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    // Extract progress data and filter by active status
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        value: item?.details?.progressName || '',
        label: item?.details?.progressName || '',
        id: item?._id || '',
      }))
      .filter(item => item.value); // Remove any empty values
  },

  async getFacing() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=facing`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    // Extract facing data and filter by active status
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        value: item?.details?.facingName || '',
        label: item?.details?.facingName || '',
        id: item?._id || '',
      }))
      .filter(item => item.value); // Remove any empty values
  },

  async getPaymentModes() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=paymentmode`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    // Extract payment mode data and filter by active status
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        value: item?.details?.paymentMode || '',
        label: item?.details?.paymentMode || '',
        id: item?._id || '',
      }))
      .filter(item => item.value); // Remove any empty values
  },

  async getProjectTypes() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=projectType`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    // Extract projectType from details.projectType in each item
    const data = Array.isArray(json?.data) ? json.data : [];
    return data
      .map(item => item?.details?.projectType)
      .filter(Boolean); // Remove any undefined/null values
  },

  async getProjectTypesWithIds() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=projectType`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        id: item?._id || '',
        label: item?.details?.projectType || '',
      }))
      .filter(item => item.id && item.label);
  },

  async getPaymentPlans() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=paymentplan`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    // Extract paymentPlan from details.paymentPlan in each item
    const data = Array.isArray(json?.data) ? json.data : [];
    return data
      .map(item => item)
      .filter(Boolean); // Remove any undefined/null values
  },

  async getBanks() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=bank`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        id: item?._id || '',
        bankName: item?.details?.bankName || '',
      }))
      .filter(item => item.id && item.bankName);
  },

  async getBudgets() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=budget`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    // Extract budgetRange from details.budgetRange, filter by active status
    return data
      .filter(item => item?.status === true && !item?.isDeleted) // Only active, non-deleted budgets
      .map(item => item?.details?.budgetRange || item?.budgetRange || '')
      .filter(Boolean); // Remove any empty values
  },

  async getVillaTypes() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=villaType`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        id: item?._id || '',
        label: item?.details?.villaType || item?.name || '',
      }))
      .filter(item => item.label);
  },

  async getAmenities() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=amenities`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => item?.details?.amenitiesName || item?.name || '')
      .filter(Boolean);
  },

  async getRowHouseTypes() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/globalMasterDetails?type=rowHouse`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    const data = Array.isArray(json?.data) ? json.data : [];
    return data
      .filter(item => item?.status === true && !item?.isDeleted)
      .map(item => ({
        id: item?._id || '',
        label: item?.details?.rowHouse || item?.name || '',
      }))
      .filter(item => item.label);
  },
};
